/**
 * KgCanvas — Knowledge Graph canvas component.
 *
 * ─── How the canvas renders (step by step) ───────────────────────────────────
 *
 * 1. MOUNT
 *    ELK (Eclipse Layout Kernel) runs asynchronously, computing x/y positions
 *    for every node based on their tier (dataSource → table → ontology →
 *    metric → report → dataStory → artifact, left to right).
 *    A loading spinner covers the canvas while ELK runs.
 *
 * 2. FIRST PAINT
 *    After ELK finishes:
 *    a. Positioned nodes are written to React Flow state.
 *    b. A brief delay (rAF + 150ms) lets React Flow's ResizeObserver measure
 *       the canvas dimensions.
 *    c. fitView() centres and zooms to fit all nodes with padding scaled to
 *       node count (more nodes = less padding so they all fit).
 *    d. The loading overlay fades out.
 *
 * 3. ONGOING RENDERING PIPELINE
 *    Raw nodes → [useKgLayout] zoom-aware collapse
 *             → [useKgGraphFilters] showLinks edge visibility
 *             → [useKgNodeSelection] selection dim + table expand
 *             → <ReactFlow> renders molecules per node type
 *
 * Entity/group/search filtering is server-driven via kg/entities query params
 * (owned by KgPage + useKgGraphEntities).
 */

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  Background,
  BackgroundVariant,
  ReactFlow,
  ReactFlowProvider,
  type ReactFlowInstance,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { rawColors } from "../../../design-system/tokens/colors";
import { KgCanvasControls } from "../KgCanvasControls";
import { KgCanvasFilterBar } from "../KgCanvasFilterBar";
import { KgNodePanel } from "../KgNodePanel";
import {
  kgNodeTypes,
  sampleEdges,
  sampleNodes,
  useKgGraphFilters,
  useKgLayout,
  useKgFullscreen,
  useKgNodeSelection,
} from "../../kg/canvas";
import { useKgCanvasFocus } from "../../kg/canvas/hooks/useKgCanvasFocus";
import { useKgLatestPipelineRun } from "../../../hooks/useKgLatestPipelineRun";
import {
  KgCanvasNavigationProvider,
} from "../../kg/canvas/KgCanvasNavigationContext";
import type { KgCanvasProps } from "./KgCanvas.types";
import {
  CanvasArea,
  ContentRow,
  LoadingLabel,
  EmptyStateMessage,
  EmptyStateOverlay,
  LoadingOverlay,
  LoadMoreButton,
  LoadMoreRow,
  Spinner,
  Wrapper,
} from "./KgCanvas.styles";

export type { KgCanvasProps } from "./KgCanvas.types";

const KgCanvasInner: React.FC<KgCanvasProps> = ({
  initialNodes = sampleNodes,
  initialEdges = sampleEdges,
  layoutQueryKey,
  collapsedZoomThreshold = 0.2,
  selectedEntities,
  onEntityChange,
  selectedGroup,
  onGroupChange,
  disabledGroups,
  showLinks,
  onShowLinksChange,
  searchValue = "",
  onSearchChange,
  isRefreshing = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  loadedNodeCount = 0,
  totalNodeCount = 0,
  emptyMessage,
}) => {
  const { wrapperRef, isFullscreen, handleToggleFullscreen } =
    useKgFullscreen();

  const pipelineStatus = useKgLatestPipelineRun();

  // Live canvas height drives column wrapping for non-table layers in the layout.
  // Rounded to 50px steps so minor resizes don't trigger a re-layout/fitView.
  const canvasAreaRef = useRef<HTMLDivElement>(null);
  const [viewportHeight, setViewportHeight] = useState<number | undefined>(
    undefined,
  );

  const applyViewportHeight = (height: number) => {
    if (height <= 0) return;
    const stepped = Math.round(height / 50) * 50;
    setViewportHeight((previous) =>
      previous === stepped ? previous : stepped,
    );
  };

  // Measure before first paint so downstream wrap uses real canvas height.
  useLayoutEffect(() => {
    const element = canvasAreaRef.current;
    if (element) applyViewportHeight(element.clientHeight);
  }, []);

  useEffect(() => {
    const element = canvasAreaRef.current;
    if (!element || typeof ResizeObserver === "undefined") return;

    applyViewportHeight(element.clientHeight);
    const observer = new ResizeObserver((entries) => {
      applyViewportHeight(entries[0]?.contentRect.height ?? 0);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const {
    edges,
    onNodesChange,
    onEdgesChange,
    isLayouting,
    isRefiningLayout,
    setZoom,
    zoomAwareNodes,
  } = useKgLayout({
    initialNodes,
    initialEdges,
    collapsedZoomThreshold,
    layoutQueryKey,
    viewportHeight,
  });

  const { visibleNodes, visibleEdges } = useKgGraphFilters(
    zoomAwareNodes,
    edges,
    showLinks,
  );

  const {
    interactiveNodes,
    interactiveEdges,
    handleNodeClick,
    handlePaneClick,
    selectedNodeId,
    clearSelection,
    focusNode,
  } = useKgNodeSelection({
    visibleNodes,
    visibleEdges,
    allEdges: edges,
  });

  const [reactFlow, setReactFlow] = useState<ReactFlowInstance | null>(null);
  const canvasNavigation = useKgCanvasFocus({ focusNode, reactFlow });

  const selectedNode =
    interactiveNodes.find((n) => n.id === selectedNodeId) ?? null;

  const loadMoreLabel =
    totalNodeCount > 0
      ? `Load more (${loadedNodeCount} of ${totalNodeCount} nodes)`
      : "Load more";

  return (
    <Wrapper ref={wrapperRef}>
      {isLayouting && (
        <LoadingOverlay>
          <Spinner />
          <LoadingLabel>Building knowledge graph…</LoadingLabel>
        </LoadingOverlay>
      )}

      <KgCanvasFilterBar
        selectedEntities={selectedEntities}
        onEntityChange={onEntityChange}
        selectedGroup={selectedGroup}
        onGroupChange={onGroupChange}
        disabledGroups={disabledGroups}
        showLinks={showLinks}
        onShowLinksChange={onShowLinksChange}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        isFullscreen={isFullscreen}
        pipelineStatus={pipelineStatus}
      />

      <KgCanvasNavigationProvider value={canvasNavigation}>
        <ContentRow>
          <CanvasArea
            ref={canvasAreaRef}
            $isRefining={isRefiningLayout || isRefreshing}
            aria-busy={isRefiningLayout || isRefreshing}
          >
            {emptyMessage && (
              <EmptyStateOverlay aria-live="polite">
                <EmptyStateMessage>{emptyMessage}</EmptyStateMessage>
              </EmptyStateOverlay>
            )}
            <ReactFlow
              nodes={interactiveNodes}
              edges={interactiveEdges}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onMove={(_, viewport) => setZoom(viewport.zoom)}
              onInit={(instance) => {
                setReactFlow(instance);
                setZoom(instance.getViewport().zoom);
              }}
              onNodeClick={handleNodeClick}
              onPaneClick={handlePaneClick}
              nodeTypes={kgNodeTypes}
              minZoom={0.05}
              maxZoom={2.5}
              proOptions={{ hideAttribution: true }}
            >
              <Background
                variant={BackgroundVariant.Dots}
                gap={24}
                size={4}
                color={rawColors.canvas.dotGrid}
              />
              <KgCanvasControls
                onExpand={handleToggleFullscreen}
                isExpanded={isFullscreen}
                panelOffset={{ left: 12, right: "auto", bottom: 12 }}
              />
            </ReactFlow>
          </CanvasArea>

          <KgNodePanel node={selectedNode} onClose={clearSelection} />
        </ContentRow>
      </KgCanvasNavigationProvider>

      {hasNextPage && onLoadMore && (
        <LoadMoreRow>
          <LoadMoreButton
            type="button"
            onClick={onLoadMore}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "Loading…" : loadMoreLabel}
          </LoadMoreButton>
        </LoadMoreRow>
      )}
    </Wrapper>
  );
};

export const KgCanvas: React.FC<KgCanvasProps> = (props) => (
  <ReactFlowProvider>
    <KgCanvasInner {...props} />
  </ReactFlowProvider>
);

export default KgCanvas;
